import { useState, useEffect, useRef, useMemo } from "react";
import { RefreshCw, MessageCircleWarning, Zap, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { BUILD_ID, BUILD_TIME, forceRefresh } from "@/lib/buildVersion";
import { getAndAdvanceChapter } from "@/lib/chapterData";
import { useVersionPoll } from "@/hooks/useVersionPoll";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { openReportDialog } from "@/components/layout/FloatingReportButton";
import HelpDrawer from "@/components/layout/HelpDrawer";
import { useScrollContainer } from "@/hooks/useScrollContainer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Version status — reuses BuildBadge logic without duplication.
 */
function useIsLatestVersion() {
  const [confirmedFresh, setConfirmedFresh] = useState(false);
  const { isStale } = useVersionPoll(60_000);

  useEffect(() => {
    const wasHardReload = sessionStorage.getItem("perform_was_hard_reload");
    if (wasHardReload) {
      setConfirmedFresh(true);
      sessionStorage.removeItem("perform_was_hard_reload");
    }
  }, []);

  if (isStale) return { status: "stale" as const };
  if (confirmedFresh) return { status: "fresh" as const };
  return { status: "unknown" as const };
}

/**
 * Mobile-only floating dock: 3 compact stacked buttons (bottom-right).
 * Replaces BuildBadge + FloatingReportButton + ScrollToTopButton on mobile.
 * Measures its own height and publishes it as --mobile-dock-h CSS variable.
 */
/** Format ISO date to human-readable local string */
function formatBuildDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function MobileFloatingDock() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/super-admin");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [serverBuild, setServerBuild] = useState<{ buildId?: string; buildTime?: string } | null>(null);
  const dockNodeRef = useRef<HTMLDivElement | null>(null);
  const { status } = useIsLatestVersion();
  const chapter = useMemo(() => getAndAdvanceChapter(BUILD_ID), []);
  const scrollRef = useScrollContainer();

  // Fetch server version when popover might be opened
  useEffect(() => {
    if (!isMobile || BUILD_ID === "dev") return;
    fetch(`/build-meta.json?_=${Date.now()}`, { cache: "no-store" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setServerBuild(data); })
      .catch(() => {});
  }, [isMobile]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handle = () => setShowScrollTop(el.scrollTop > 20);
    handle();
    el.addEventListener("scroll", handle, { passive: true });
    return () => el.removeEventListener("scroll", handle);
  }, [scrollRef]);

  // Publish dock height as CSS variable — proper cleanup guaranteed
  useEffect(() => {
    if (!isMobile) {
      document.documentElement.style.removeProperty("--mobile-dock-h");
      return;
    }
    const node = dockNodeRef.current;
    if (!node) return;

    const update = () => {
      document.documentElement.style.setProperty(
        "--mobile-dock-h",
        `${Math.ceil(node.getBoundingClientRect().height)}px`
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);

    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--mobile-dock-h");
    };
  }, [isMobile]);

  if (!isMobile) return null;

  const handleScrollTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    forceRefresh();
  };

  const isFresh = status === "fresh";
  const isStale = status === "stale";

  return (
    <div
      ref={dockNodeRef}
      className={cn(
        "fixed right-3 z-50 flex flex-col items-center gap-2",
        "pb-[env(safe-area-inset-bottom)]",
        isAdminRoute ? "bottom-[5.5rem]" : "bottom-5"
      )}
    >
      {showScrollTop && (
        <button
          onClick={handleScrollTop}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary transition-colors hover:bg-primary/25"
          aria-label="Remonter en haut"
        >
          <Zap className="h-4 w-4 rotate-180" />
        </button>
      )}

      {user && (
        <button
          onClick={openReportDialog}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-card/80 border border-border hover:bg-primary/15 transition-all duration-300"
          aria-label="Signaler un bug"
        >
          <MessageCircleWarning className="h-4 w-4 text-primary/70 hover:text-primary transition-colors" />
        </button>
      )}

      {user && <HelpDrawer />}

      {/* Version-aware refresh button with popover */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full border transition-colors",
              isStale
                ? "bg-amber-950/80 border-amber-600/50 text-amber-400"
                : isFresh
                  ? "bg-emerald-950/80 border-emerald-700/50 text-emerald-400"
                  : "bg-card/80 border-border text-muted-foreground"
            )}
            aria-label="Version et mise à jour"
          >
            {isStale ? (
              <AlertTriangle className="h-3.5 w-3.5 animate-pulse" />
            ) : isFresh ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <Info className="h-3.5 w-3.5" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          className="w-56 p-3 space-y-2"
        >
          <div className="flex items-center gap-2">
            {isStale ? (
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
            ) : isFresh ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
            ) : (
              <Info className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className={cn(
              "text-xs font-semibold capitalize",
              isStale ? "text-amber-300" : isFresh ? "text-emerald-300" : "text-foreground"
            )}>
              {chapter.chapter}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground space-y-0.5">
            <p>{chapter.arc} · V{chapter.version}</p>
            <p>Build : {BUILD_ID.slice(0, 8)}</p>
            <p>Date : {formatBuildDate(BUILD_TIME)}</p>
            {serverBuild?.buildTime && serverBuild.buildId !== BUILD_ID && (
              <p className="text-amber-400">
                Serveur : {formatBuildDate(serverBuild.buildTime)}
              </p>
            )}
            <p className={cn(
              "font-medium",
              isStale ? "text-amber-400" : isFresh ? "text-emerald-400" : "text-muted-foreground"
            )}>
              {isStale ? "⚠ Nouvelle version disponible" : isFresh ? "✓ Version à jour" : "Version non vérifiée"}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={cn(
              "flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
              isStale
                ? "bg-amber-600/20 text-amber-300 hover:bg-amber-600/30"
                : "bg-secondary text-foreground hover:bg-muted"
            )}
          >
            <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
            {isStale ? "Mettre à jour" : "Forcer la mise à jour"}
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
